import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { trackEvent } from '../components/Analytics';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { RelatedTools } from '../components/RelatedTools';
import {
  ArrowRight, Drop, Timer, MapPin, Warning, CheckCircle,
  Plus, Trash, Calculator, Info, ArrowClockwise, Leaf,
  Lightning, Plugs,
} from '@phosphor-icons/react';

/**
 * Water Budget — public page that turns ET0 + rainfall + city restrictions
 * into a plain-English watering plan. Solves the "tuna-can guessing game"
 * problem for everyone in a restriction zone.
 */

const DEFAULT_ZONE = {
  name: 'Zone 1',
  nozzle_type: 'fixed_spray',
  precipitation_rate_in_hr: '',
  square_footage: 1000,
};

const GRASS_OPTIONS = [
  { id: 'bermuda', label: 'Bermuda' },
  { id: 'zoysia', label: 'Zoysia' },
  { id: 'st_augustine', label: 'St. Augustine' },
  { id: 'tall_fescue', label: 'Tall Fescue' },
  { id: 'kentucky_bluegrass', label: 'Kentucky Bluegrass' },
  { id: 'ryegrass', label: 'Ryegrass' },
  { id: 'centipede', label: 'Centipede' },
  { id: 'bahiagrass', label: 'Bahiagrass' },
  { id: 'mixed_cool', label: 'Mixed cool-season' },
  { id: 'mixed_warm', label: 'Mixed warm-season' },
];

const SOIL_OPTIONS = [
  { id: 'sandy', label: 'Sandy / sandy loam' },
  { id: 'loam', label: 'Loam (ideal)' },
  { id: 'clay_loam', label: 'Clay loam (most CTX neighborhoods)' },
  { id: 'clay', label: 'Heavy clay (Blackland Prairie)' },
  { id: 'compacted_clay', label: 'Compacted clay (new construction / high-traffic)' },
  { id: 'hydrophobic_clay', label: 'Hydrophobic clay (post-drought / water beads up)' },
  { id: 'caliche_shallow', label: 'Shallow soil over caliche' },
];

const NOZZLE_OPTIONS = [
  { id: 'fixed_spray', label: 'Fixed Spray (~1.5 in/hr)' },
  { id: 'rotor', label: 'Rotor (~0.5 in/hr)' },
  { id: 'mp_rotator', label: 'MP Rotator (~0.4 in/hr)' },
  { id: 'drip', label: 'Drip (~0.5 in/hr)' },
  { id: 'bubbler', label: 'Bubbler (~1.0 in/hr)' },
];

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5 text-sm">
    <span className="text-zinc-400 text-xs uppercase tracking-wider font-medium">{label}</span>
    {children}
    {hint && <span className="text-zinc-600 text-xs leading-relaxed">{hint}</span>}
  </label>
);

const CatchCupHelper = ({ onMeasured, onClose }) => {
  const [ml, setMl] = useState(50);
  const [minutes, setMinutes] = useState(10);
  const [diameter, setDiameter] = useState(3);
  const [result, setResult] = useState(null);

  const measure = async () => {
    try {
      const r = await axios.post(
        `${API_URL}/api/water-budget/catch-cup?volume_ml=${ml}&run_time_minutes=${minutes}&cup_diameter_in=${diameter}`,
      );
      setResult(r.data);
    } catch (e) {
      setResult({ error: 'Check your inputs and try again.' });
    }
  };

  return (
    <div className="mt-3 p-4 bg-zinc-950/60 border border-zinc-800 rounded-lg space-y-3" data-testid="catch-cup-helper">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-zinc-200 text-sm font-medium">Tuna-can catch test</div>
          <div className="text-zinc-500 text-xs mt-0.5 leading-relaxed">
            Place 3–5 tuna cans on this zone. Run for 10 minutes. Measure how many ml each collected
            (kitchen measuring cup works). Enter the average below.
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-zinc-300 text-xs" data-testid="cc-close">close</button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Field label="Avg. ml in cup">
          <input
            type="number" value={ml} onChange={(e) => setMl(Number(e.target.value))}
            className="px-3 py-2 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono text-sm"
            data-testid="cc-ml"
          />
        </Field>
        <Field label="Run time (min)">
          <input
            type="number" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}
            className="px-3 py-2 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono text-sm"
            data-testid="cc-min"
          />
        </Field>
        <Field label="Cup diameter (in)">
          <input
            type="number" step="0.1" value={diameter} onChange={(e) => setDiameter(Number(e.target.value))}
            className="px-3 py-2 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono text-sm"
            data-testid="cc-diameter"
          />
        </Field>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={measure}
          className="px-3 py-1.5 bg-zinc-100 hover:bg-white text-zinc-900 text-xs font-medium rounded"
          data-testid="cc-measure"
        >
          Calculate PR
        </button>
        {result && !result.error && (
          <>
            <span className="text-zinc-300 text-sm font-mono">
              {result.precipitation_rate_in_hr} in/hr
            </span>
            <button
              onClick={() => { onMeasured(result.precipitation_rate_in_hr); onClose(); }}
              className="ml-auto px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded"
              data-testid="cc-use"
            >
              Use this
            </button>
          </>
        )}
      </div>
      {result && !result.error && (
        <div className="text-xs text-zinc-500 italic">{result.interpretation}</div>
      )}
      {result?.error && <div className="text-rose-400 text-xs">{result.error}</div>}
    </div>
  );
};

const WaterBudgetPage = () => {
  const [presets, setPresets] = useState([]);
  const [location, setLocation] = useState({ latitude: 30.568, longitude: -97.613 });
  const [zipInput, setZipInput] = useState('');
  const [grass, setGrass] = useState('bermuda');
  const [soil, setSoil] = useState('compacted_clay');
  const [stationId, setStationId] = useState('KTXROUND582');
  const [preset, setPreset] = useState('round_rock_tx_even');
  const [zones, setZones] = useState([{ ...DEFAULT_ZONE, name: 'Front yard', square_footage: 1500 }]);
  const [ccOpenForIdx, setCcOpenForIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Water Budget — TurfPulse';
    axios.get(`${API_URL}/api/water-budget/presets`).then((r) => setPresets(r.data.presets || []));
  }, []);

  const geocodeZip = async () => {
    if (!zipInput.trim()) return;
    try {
      const r = await axios.get(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(zipInput)}&count=1&country=US`
      );
      const first = r.data?.results?.[0];
      if (first) {
        setLocation({ latitude: first.latitude, longitude: first.longitude });
      }
    } catch (e) {
      // silent
    }
  };

  const updateZone = (idx, patch) => {
    setZones((zs) => zs.map((z, i) => (i === idx ? { ...z, ...patch } : z)));
  };

  const addZone = () => {
    setZones((zs) => [
      ...zs,
      { ...DEFAULT_ZONE, name: `Zone ${zs.length + 1}` },
    ]);
  };

  const removeZone = (idx) => {
    setZones((zs) => zs.filter((_, i) => i !== idx));
  };

  const compute = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        latitude: location.latitude,
        longitude: location.longitude,
        grass_type: grass,
        soil_type: soil,
        station_id: stationId.trim() || null,
        compliance: { preset },
        zones: zones.map((z) => ({
          name: z.name || 'Zone',
          nozzle_type: z.nozzle_type,
          precipitation_rate_in_hr: z.precipitation_rate_in_hr
            ? Number(z.precipitation_rate_in_hr)
            : null,
          square_footage: Number(z.square_footage) || 1000,
        })),
      };
      const r = await axios.post(`${API_URL}/api/water-budget/compute`, payload);
      setResult(r.data);
      // Scroll the result panel into view — on mobile especially, the result
      // appears below the button and users miss it without auto-scroll.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="wb-result"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      // GA4: activation "aha" moment — user got a real water budget number for their yard.
      trackEvent('water_budget_calculated', {
        zip: location?.zip || '',
        grass_type: grass,
        soil_type: soil,
        zone_count: zones.length,
        has_byos_station: !!stationId,
        total_runtime_min: r.data?.total_runtime_min || 0,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Calculation failed. Try again.');
    } finally {
      setLoading(false);
    }
  }, [location, grass, soil, stationId, preset, zones]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="water-budget-page">
      <SEO
        title="Water Budget Calculator — catch-cup + ET0 + nozzle-swap ROI"
        description="Calibrate your irrigation by the math: 7-day ET0 + rainfall + your catch-cup precip rate + DU penalty. Free tool, no sign-up. Built for enthusiasts who are done guessing."
        keywords="water budget calculator, catch cup test, irrigation calibration, ET0 calculator, sprinkler precipitation rate, distribution uniformity, lawn watering calculator"
        path="/water-budget"
      />
      <BreadcrumbJsonLd items={[
        { label: 'Home', path: '/' },
        { label: 'Water Budget', path: '/water-budget' },
      ]} />
      {/* Hero */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-5xl mx-auto px-5 md:px-8 pt-16 pb-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-emerald-500 mb-4">
            <Drop size={14} weight="fill" />
            <span>Water Budget</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="wb-title">
            Stop guessing with tuna cans.
            <br />
            <span className="text-emerald-500">Start measuring what matters.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base max-w-2xl leading-relaxed">
            Your city tells you <em>when</em> you can water. Nobody tells you <em>how much</em>. This
            calculator combines real ET data from your location, measured rainfall from your nearest
            weather station, and your sprinkler output — and gives you an answer in minutes.
          </p>
        </div>
      </section>

      {/* Existing saved schedules — visible whenever user has any */}
      <SavedSchedulesPanel />

      {/* Form */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10 grid md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="space-y-3">
            <h2 className="text-zinc-300 text-xs uppercase tracking-[0.2em]">Your Lawn</h2>
            <Field label="ZIP code or city" hint="We use this for ET and compliance presets.">
              <div className="flex gap-2">
                <input
                  value={zipInput}
                  onChange={(e) => setZipInput(e.target.value)}
                  onBlur={geocodeZip}
                  placeholder="e.g. 78665"
                  className="flex-1 px-4 py-2.5 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono"
                  data-testid="wb-zip"
                />
                <button
                  onClick={geocodeZip}
                  className="px-3 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded border border-zinc-700"
                >
                  Set
                </button>
              </div>
            </Field>
            <div className="text-xs text-zinc-500 font-mono">
              Using: {location.latitude.toFixed(3)}, {location.longitude.toFixed(3)}
            </div>
            <Field label="Grass type">
              <select
                value={grass}
                onChange={(e) => setGrass(e.target.value)}
                className="px-4 py-2.5 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none"
                data-testid="wb-grass"
              >
                {GRASS_OPTIONS.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Soil type" hint="Compacted clay is common in new construction — needs shorter cycles + amendments.">
              <select
                value={soil}
                onChange={(e) => setSoil(e.target.value)}
                className="px-4 py-2.5 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none"
                data-testid="wb-soil"
              >
                {SOIL_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Weather Underground station (optional)" hint="Your Ecowitt or neighbor's station for real local rainfall.">
              <input
                value={stationId}
                onChange={(e) => setStationId(e.target.value.toUpperCase())}
                placeholder="e.g. KTXROUND582"
                className="px-4 py-2.5 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono"
                data-testid="wb-station"
              />
            </Field>
          </div>

          <div className="space-y-3">
            <h2 className="text-zinc-300 text-xs uppercase tracking-[0.2em]">City Rules</h2>
            <Field label="Watering restriction preset" hint="Don't see your city? Pick 'No restrictions' and schedule manually.">
              <select
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
                className="px-4 py-2.5 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none"
                data-testid="wb-preset"
              >
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-zinc-300 text-xs uppercase tracking-[0.2em]">Your Zones</h2>
            <button
              onClick={addZone}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-xs text-zinc-300"
              data-testid="wb-add-zone"
            >
              <Plus size={14}/> Add zone
            </button>
          </div>

          {zones.map((z, idx) => (
            <div key={idx} className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-lg space-y-3" data-testid={`wb-zone-${idx}`}>
              <div className="flex items-center gap-2">
                <input
                  value={z.name}
                  onChange={(e) => updateZone(idx, { name: e.target.value })}
                  className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 outline-none text-sm"
                  placeholder="Zone name"
                />
                {zones.length > 1 && (
                  <button
                    onClick={() => removeZone(idx)}
                    className="p-1.5 text-zinc-500 hover:text-rose-400"
                    data-testid={`wb-remove-zone-${idx}`}
                  >
                    <Trash size={16}/>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Nozzle type">
                  <select
                    value={z.nozzle_type}
                    onChange={(e) => updateZone(idx, { nozzle_type: e.target.value })}
                    className="px-3 py-2 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none text-sm"
                  >
                    {NOZZLE_OPTIONS.map((n) => (
                      <option key={n.id} value={n.id}>{n.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Sq ft">
                  <input
                    type="number"
                    value={z.square_footage}
                    onChange={(e) => updateZone(idx, { square_footage: e.target.value })}
                    className="px-3 py-2 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono text-sm"
                  />
                </Field>
              </div>
              <Field label="Measured PR (optional, in/hr)" hint="Leave blank to use nozzle default. Use catch-cup test for real accuracy.">
                <div className="flex gap-2">
                  <input
                    type="number" step="0.1"
                    value={z.precipitation_rate_in_hr}
                    onChange={(e) => updateZone(idx, { precipitation_rate_in_hr: e.target.value })}
                    placeholder="auto"
                    className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 rounded text-zinc-100 outline-none font-mono text-sm"
                    data-testid={`wb-pr-${idx}`}
                  />
                  <button
                    onClick={() => setCcOpenForIdx(ccOpenForIdx === idx ? null : idx)}
                    className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-xs text-zinc-300 whitespace-nowrap"
                    data-testid={`wb-cc-toggle-${idx}`}
                  >
                    <Calculator size={14} className="inline mr-1"/> Catch-cup
                  </button>
                </div>
              </Field>
              {ccOpenForIdx === idx && (
                <CatchCupHelper
                  onMeasured={(pr) => updateZone(idx, { precipitation_rate_in_hr: pr })}
                  onClose={() => setCcOpenForIdx(null)}
                />
              )}
            </div>
          ))}

          <button
            onClick={compute}
            disabled={loading || zones.length === 0}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-medium rounded-lg transition-colors"
            data-testid="wb-compute"
          >
            {loading ? (
              <><ArrowClockwise size={18} className="animate-spin" /> Computing…</>
            ) : (
              <>Compute my water budget <ArrowRight size={18} weight="bold" /></>
            )}
          </button>
          {error && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded text-rose-300 text-sm" data-testid="wb-error">
              {error}
            </div>
          )}
        </div>
      </section>

      {/* Result */}
      {result && <WaterBudgetResult data={result} grassType={grass} soilType={soil} />}

      {/* Educational footer */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-12 border-t border-zinc-800/80 text-zinc-500 text-xs leading-relaxed space-y-2">
        <p><strong className="text-zinc-300">About the math:</strong> ET0 is reference evapotranspiration — the industry-standard measure of how much water your turf is losing per day. We pull it from Open-Meteo (FAO-56 Penman-Monteith model, same formula used by NRCS, USDA, and ag consultants). Multiplied by a crop coefficient (Kc) for your grass type, it gives turf demand. We subtract 75% of measured rainfall (effective rain) to get the deficit. Then we divide by your zone's measured precipitation rate to get runtime in minutes.</p>
        <p><strong className="text-zinc-300">About the catch-cup test:</strong> Place 3–5 identical cups (tuna cans are 3" diameter — the industry standard) evenly across a zone. Run the zone for 10 minutes. Measure the volume each cup collected. Average them, then use our Catch-Cup helper to compute PR. Do this once per zone — the number doesn't change unless you swap nozzles.</p>
        <p><strong className="text-zinc-300">Compliance data is a guide, not legal advice.</strong> Always confirm your exact watering day and blackout hours with your city's water utility. Our presets are good starting points.</p>
      </section>
      <div className="max-w-5xl mx-auto px-5 md:px-8 pb-12">
        <RelatedTools currentPath="/water-budget" />
      </div>
    </div>
  );
};

const Stat = ({ label, value, unit = '', accent = false }) => (
  <div className={`flex flex-col gap-1 p-4 rounded-lg border ${accent ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-900/60 border-zinc-800'}`}>
    <div className="text-zinc-500 text-xs uppercase tracking-wider">{label}</div>
    <div className={`text-2xl font-semibold font-mono ${accent ? 'text-emerald-300' : 'text-zinc-100'}`}>
      {value}{unit && <span className="ml-1 text-sm text-zinc-500 font-normal">{unit}</span>}
    </div>
  </div>
);

const WaterBudgetResult = ({ data, grassType, soilType }) => {
  const { budget, compliance, zones, et, rain, grass, advisories } = data;
  const deficitLow = budget.per_session_deficit_in < 0.05;
  const hasBlocker = (advisories || []).some((a) => a.severity === 'block');
  return (
    <section className="max-w-5xl mx-auto px-5 md:px-8 py-12 space-y-8" data-testid="wb-result">
      {/* Advisories */}
      {advisories && advisories.length > 0 && (
        <div className="space-y-2" data-testid="wb-advisories">
          {advisories.map((a, i) => (
            <div
              key={i}
              className={`p-4 rounded-lg border flex items-start gap-3 ${
                a.severity === 'block'
                  ? 'bg-rose-500/10 border-rose-500/40 text-rose-100'
                  : 'bg-amber-500/10 border-amber-500/40 text-amber-100'
              }`}
              data-testid={`wb-advisory-${a.code}`}
            >
              <Warning size={20} className="flex-shrink-0 mt-0.5"/>
              <div>
                <div className="text-xs uppercase tracking-wider font-medium opacity-80">
                  {a.severity === 'block' ? 'Skip this cycle' : 'Heads up'}
                </div>
                <div className="text-sm mt-0.5">{a.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Headline */}
      <div className={`p-6 rounded-xl border ${
        hasBlocker
          ? 'border-rose-500/40 bg-rose-500/5'
          : deficitLow
          ? 'border-zinc-700 bg-zinc-900/60'
          : 'border-emerald-500/40 bg-emerald-500/5'
      }`}>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 mb-2 flex items-center gap-2">
          <CheckCircle size={14} weight="fill"/> Your plan
        </div>
        <div className="text-2xl md:text-3xl font-semibold text-zinc-100 leading-tight" data-testid="wb-headline">
          {hasBlocker ? (
            <>Don't water on {budget.next_allowed_day_name} — {advisories.find((a) => a.severity === 'block').code.replace('_', ' ')}.</>
          ) : deficitLow ? (
            <>Skip watering on {budget.next_allowed_day_name}. Recent rainfall covered your lawn's demand.</>
          ) : (
            <>Run your zones on <span className="text-emerald-300">{budget.next_allowed_day_name}</span> to put down{' '}
              <span className="text-emerald-300 font-mono">{budget.per_session_deficit_in.toFixed(2)}"</span> per session,{' '}
              <span className="text-emerald-300 font-mono">{budget.sessions_per_week}×</span> per week.</>
          )}
        </div>
        {compliance.currently_in_blackout && (
          <div className="mt-3 flex items-center gap-2 text-amber-300 text-sm">
            <Warning size={16}/> Right now you're in the {compliance.blackout_start_hour}:00–{compliance.blackout_end_hour}:00 blackout window.
          </div>
        )}
        {grass.kc_seasonal_multiplier && grass.kc_seasonal_multiplier < 0.7 && (
          <div className="mt-3 text-zinc-400 text-sm">
            ℹ️ {grass.type.replace('_', ' ')} is only {Math.round(grass.kc_seasonal_multiplier * 100)}% active this month —
            water demand is {(1 - grass.kc_seasonal_multiplier) * 100 | 0}% lower than peak season.
          </div>
        )}
      </div>

      {/* Zone runtimes */}
      <div>
        <h3 className="text-zinc-300 text-xs uppercase tracking-[0.2em] mb-4">Zone runtimes (per session)</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {zones.map((z, idx) => {
            const cs = z.cycle_and_soak || { cycles: 1, minutes_per_cycle: z.runtime_minutes, soak_minutes_between: 0, total_wall_clock_minutes: z.runtime_minutes };
            const multiCycle = cs.cycles > 1;
            return (
              <div key={idx} className="p-5 bg-zinc-900/60 border border-zinc-800 rounded-lg" data-testid={`wb-zone-result-${idx}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-zinc-100 font-medium">{z.name}</div>
                    <div className="text-zinc-500 text-xs mt-0.5">
                      {z.nozzle_type.replace('_', ' ')} · {z.precipitation_rate_in_hr} in/hr · {Math.round(z.system_efficiency * 100)}% eff.
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-semibold text-emerald-300 font-mono">{z.runtime_minutes}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-wider">min / session</div>
                  </div>
                </div>

                {multiCycle ? (
                  <div className="mt-4 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-md" data-testid={`wb-cycle-soak-${idx}`}>
                    <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-wider mb-1">
                      <Timer size={12} weight="fill"/> Cycle & Soak
                    </div>
                    <div className="text-zinc-200 text-sm font-medium">
                      {cs.cycles}× {cs.minutes_per_cycle}-min cycles
                    </div>
                    <div className="text-zinc-500 text-xs mt-0.5">
                      {cs.soak_minutes_between} min rest between · ~{cs.total_wall_clock_minutes} min wall-clock total
                    </div>
                    <div className="text-zinc-600 text-[11px] mt-1.5 italic">
                      Prevents runoff on clay or slopes. Most modern controllers support this natively.
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-zinc-500 text-xs flex items-center gap-1.5">
                    <CheckCircle size={12} weight="fill" className="text-emerald-500"/>
                    Single-run, no cycle needed
                  </div>
                )}

                {z.nozzle_swap_recommendation && (
                  <div className="mt-4 p-4 bg-amber-500/5 border border-amber-500/30 rounded-md" data-testid={`wb-swap-rec-${idx}`}>
                    <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-wider mb-2">
                      <ArrowRight size={12} weight="fill"/> Upgrade available
                    </div>
                    <div className="text-zinc-100 text-sm font-medium">
                      Swap fixed spray → {z.nozzle_swap_recommendation.recommended_model}
                    </div>
                    <div className="text-zinc-400 text-xs mt-2 leading-relaxed">
                      {z.nozzle_swap_recommendation.why}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                      <div className="p-2 bg-zinc-900/60 rounded">
                        <div className="text-zinc-600">PR drops</div>
                        <div className="text-zinc-200 font-mono">
                          {z.nozzle_swap_recommendation.current_nozzle_pr} → {z.nozzle_swap_recommendation.recommended_pr} in/hr
                        </div>
                      </div>
                      <div className="p-2 bg-zinc-900/60 rounded">
                        <div className="text-zinc-600">Annual savings</div>
                        <div className="text-emerald-300 font-mono">
                          {z.nozzle_swap_recommendation.gallons_saved_per_year.toLocaleString()} gal
                          <span className="text-zinc-500 text-[10px] ml-1">
                            ≈ ${z.nozzle_swap_recommendation.dollars_saved_per_year}/yr
                          </span>
                        </div>
                      </div>
                    </div>
                    <a
                      href={z.nozzle_swap_recommendation.product_url}
                      target="_blank"
                      rel="nofollow sponsored noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded text-xs font-medium transition-colors"
                      data-testid={`wb-swap-shop-${idx}`}
                    >
                      Shop {z.nozzle_swap_recommendation.recommended_model.split(' (')[0]} on Amazon →
                    </a>
                    <div className="text-[10px] text-zinc-600 mt-2 italic">
                      Retrofits into existing Hunter PROS-04 bodies. Most Rainbird 1800 & Toro 570 bodies work with an adapter. DIY install ~10 min/head.
                    </div>
                  </div>
                )}

                {z.estimated_gallons > 0 && (
                  <div className="text-zinc-600 text-xs mt-2">≈ {z.estimated_gallons} gal per session</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Data details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Past 7d ET0" value={et.past_7_day_et0_in.toFixed(2)} unit="in"/>
        <Stat label={`Crop demand (${grass.type})`} value={et.past_7_day_crop_demand_in.toFixed(2)} unit="in"/>
        <Stat label="Effective rain" value={rain.effective_rain_in.toFixed(2)} unit="in"/>
        <Stat label="Per-session need" value={budget.per_session_deficit_in.toFixed(2)} unit="in" accent/>
      </div>

      {/* Soil profile + amendments (the compaction fix) */}
      {data.soil && (
        <div className="p-5 bg-zinc-950/60 border border-zinc-800 rounded-lg space-y-3" data-testid="wb-soil-block">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">Soil Profile</div>
              <div className="text-zinc-100 font-medium mt-1">{data.soil.label}</div>
              <div className="text-zinc-500 text-xs mt-0.5 font-mono">
                Intake {data.soil.intake_rate_in_hr} in/hr · holds {data.soil.water_holding_in_per_ft}"/ft
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
              data.soil.compaction_risk === 'severe' ? 'bg-rose-500/15 text-rose-300 border border-rose-500/30' :
              data.soil.compaction_risk === 'high' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' :
              data.soil.compaction_risk === 'moderate' ? 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/30' :
              'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
            }`}>
              {data.soil.compaction_risk} compaction risk
            </span>
          </div>

          {data.soil.notes && (
            <p className="text-zinc-400 text-sm leading-relaxed">{data.soil.notes}</p>
          )}

          {data.soil.amendments && data.soil.amendments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="text-xs uppercase tracking-wider text-emerald-500 mb-2 flex items-center gap-1.5">
                <Leaf size={12} weight="fill"/> Recommended treatments
              </div>
              <ul className="space-y-2 text-sm text-zinc-300" data-testid="wb-amendments-list">
                {data.soil.amendments.map((a, i) => {
                  // Support both old string-shaped and new object-shaped amendments
                  const text = typeof a === 'string' ? a : a.text;
                  const product = typeof a === 'object' ? a.product : null;
                  return (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-emerald-500 flex-shrink-0 mt-1">•</span>
                      <div className="flex-1">
                        <span>{text}</span>
                        {product && (
                          <a
                            href={product.url}
                            target="_blank"
                            rel="nofollow sponsored noopener noreferrer"
                            data-testid={`wb-amendment-shop-${i}`}
                            className="inline-flex items-center gap-1.5 ml-2 px-2.5 py-0.5 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 rounded-full transition-colors"
                          >
                            Shop {product.label} →
                          </a>
                        )}
                        {product?.why && (
                          <div className="text-zinc-500 text-xs mt-0.5">{product.why}</div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="text-[10px] text-zinc-600 mt-3 italic leading-relaxed">
                Product links are Amazon affiliate — TurfPulse earns a small commission on qualifying purchases at no extra cost to you. We only recommend products we'd use ourselves.
              </div>
            </div>
          )}

          {/* Water waste / savings estimate */}
          {data.soil.waste_estimate && data.soil.waste_estimate.gallons_saved_per_year > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="text-xs uppercase tracking-wider text-emerald-500 mb-2">💧 Water saved after amendments</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-zinc-500 text-xs">Today's runoff</div>
                  <div className="text-rose-300 font-mono text-lg">{data.soil.waste_estimate.current_waste_pct}%</div>
                </div>
                <div>
                  <div className="text-zinc-500 text-xs">After amendments</div>
                  <div className="text-emerald-300 font-mono text-lg">{data.soil.waste_estimate.improved_waste_pct}%</div>
                </div>
                <div>
                  <div className="text-zinc-500 text-xs">Annual savings</div>
                  <div className="text-zinc-100 font-mono text-lg">
                    {data.soil.waste_estimate.gallons_saved_per_year.toLocaleString()} gal
                  </div>
                  <div className="text-zinc-500 text-[10px]">≈ ${data.soil.waste_estimate.dollars_saved_per_year}/yr</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compliance details */}
      <div className="p-5 bg-zinc-950/60 border border-zinc-800 rounded-lg">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Compliance window</div>
        <div className="text-zinc-200">{compliance.label}</div>
        <div className="text-zinc-500 text-sm mt-1">
          Allowed days: {compliance.allowed_days.join(', ')}
          {compliance.blackout_start_hour != null && (
            <> · Blackout: {compliance.blackout_start_hour}:00–{compliance.blackout_end_hour}:00</>
          )}
        </div>
        {compliance.notes && <div className="text-zinc-600 text-xs mt-2 italic">{compliance.notes}</div>}
      </div>

      {/* ET forecast */}
      {et.daily_et0_forecast && et.daily_et0_forecast.length > 0 && (
        <div>
          <h3 className="text-zinc-300 text-xs uppercase tracking-[0.2em] mb-3">ET0 forecast (next 7 days)</h3>
          <div className="flex items-end gap-2 h-32 bg-zinc-900/40 border border-zinc-800 rounded-lg p-4">
            {et.daily_et0_forecast.map((d) => {
              const h = Math.min(100, Math.max(6, d.et0_in * 280));
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-xs text-zinc-500 font-mono">{d.et0_in.toFixed(2)}</div>
                  <div style={{ height: `${h}%` }} className="w-full bg-emerald-500/70 rounded-t"/>
                  <div className="text-[10px] text-zinc-600 uppercase">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PWS credit */}
      {rain.pws_station_id && rain.pws_rain_24h_in !== null && (
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <MapPin size={12}/> Last 24h rainfall from <Link to={`/byos`} className="text-emerald-400 hover:underline font-mono">{rain.pws_station_id}</Link>: {rain.pws_rain_24h_in}"
        </div>
      )}

      {/* Manual schedule coverage — only renders when user is logged in
          AND has a saved sprinkler schedule. Shows the gap between what
          the user is currently applying vs what TurfPulse recommends. */}
      <ManualCoverageBlock perSessionDeficit={budget.per_session_deficit_in} sessionsPerWeek={budget.sessions_per_week} />

      {/* Apply this plan directly to your Rachio (only renders if Rachio is connected) */}
      <ApplyToRachioPanel wbZones={zones} grassType={grassType} soilType={soilType} />
    </section>
  );
};

/**
 * SplitPreview — calls /api/irrigation/schedules/preview-split with the
 * current form state and shows a tiny "Tue: ~9 min · Thu: ~21 min" table.
 * Helps the user *see* the smart_gap effect before they save anything.
 */
const SplitPreview = ({ schedDays, schedTime, splitStrategy, schedName, runnable, zoneById }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (schedDays.length === 0 || runnable.length === 0) {
      setData(null);
      return;
    }
    const payload = {
      name: schedName || 'preview',
      zones: runnable.map((x, i) => ({
        rachio_zone_id: x.rachioId,
        duration_minutes: x.wbz.runtime_minutes,
        sort_order: i + 1,
        name: zoneById[x.rachioId] || x.wbz.name,
      })),
      days_of_week: [...schedDays].sort((a, b) => a - b),
      start_time_local: schedTime,
      timezone: 'America/Chicago',
      split_strategy: splitStrategy,
    };
    (async () => {
      try {
        const r = await axios.post(
          `${API_URL}/api/irrigation/schedules/preview-split`, payload,
        );
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedDays.join(','), schedTime, splitStrategy, runnable.length]);

  if (!data || !data.breakdown || data.breakdown.length === 0) return null;

  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-md p-2.5 space-y-1.5 mt-2" data-testid="wb-split-preview">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Per-day preview</div>
      <table className="w-full text-xs">
        <tbody>
          {data.breakdown.map((d) => (
            <tr key={d.day_of_week} className="border-b border-zinc-900 last:border-0">
              <td className="py-1 text-zinc-400 font-medium">{d.day_name}</td>
              <td className="py-1 text-zinc-500 text-[10px]">→ {d.gap_after_days}d gap</td>
              <td className="py-1 text-right">
                <span className={
                  d.multiplier > 1.05 ? 'text-emerald-400 font-mono font-semibold' :
                  d.multiplier < 0.95 ? 'text-amber-400 font-mono' :
                  'text-zinc-300 font-mono'
                }>
                  {d.total_minutes} min
                </span>
                {Math.abs(d.multiplier - 1) > 0.05 && (
                  <span className="text-[9px] text-zinc-500 ml-1">×{d.multiplier.toFixed(2)}</span>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan="2" className="pt-1.5 text-[10px] text-zinc-500 uppercase tracking-wider">Weekly total</td>
            <td className="pt-1.5 text-right text-zinc-400 font-mono text-xs">{data.weekly_total_minutes} min</td>
          </tr>
        </tbody>
      </table>
      {splitStrategy === 'smart_gap' && data.breakdown.length >= 2 && (
        <div className="text-[10px] text-zinc-500 italic leading-relaxed pt-1">
          Deeper days run before the longer dry stretch — encourages deeper roots and better drought tolerance.
        </div>
      )}
    </div>
  );
};

/**
 * ManualCoverageBlock — silent "honest gap" panel between what the user's
 * current manual sprinkler schedule applies vs what TurfPulse recommends.
 * Self-hides for logged-out users and users with no saved schedule.
 */
const ManualCoverageBlock = ({ perSessionDeficit, sessionsPerWeek }) => {
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/water-budget/manual-coverage?per_session_deficit_in=${perSessionDeficit}&sessions_per_week=${sessionsPerWeek}`,
          { withCredentials: true },
        );
        if (alive) setCoverage(r.data);
      } catch {
        if (alive) setCoverage(null);
      }
    })();
    return () => { alive = false; };
  }, [perSessionDeficit, sessionsPerWeek]);

  if (!coverage || coverage.verdict === 'no_schedule') return null;

  const palette = coverage.verdict === 'good'
    ? { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', accent: 'text-emerald-400', meter: 'bg-emerald-500' }
    : coverage.verdict === 'over'
    ? { border: 'border-blue-500/30', bg: 'bg-blue-500/5', accent: 'text-blue-400', meter: 'bg-blue-500' }
    : { border: 'border-amber-500/30', bg: 'bg-amber-500/5', accent: 'text-amber-400', meter: 'bg-amber-500' };
  const meterPct = Math.min(100, coverage.coverage_pct || 0);

  return (
    <div className={`border ${palette.border} ${palette.bg} rounded-xl p-5 space-y-3`} data-testid="wb-manual-coverage">
      <div className="flex items-start gap-2">
        <Calculator size={18} className={palette.accent + ' mt-0.5'} weight="fill" />
        <div className="flex-1">
          <div className={`${palette.accent} text-xs uppercase tracking-[0.2em] font-semibold`}>
            Your current schedule vs TurfPulse
          </div>
          <div className="text-zinc-200 font-semibold mt-0.5">
            Coverage: <span className="font-mono">{coverage.coverage_pct}%</span> of recommended demand
          </div>
        </div>
      </div>

      {/* Coverage meter */}
      <div className="h-2 w-full bg-zinc-900 rounded-full overflow-hidden" data-testid="wb-coverage-meter">
        <div
          className={`h-full ${palette.meter} rounded-full transition-all duration-500`}
          style={{ width: `${meterPct}%` }}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
        <div className="p-2 bg-zinc-900/60 rounded">
          <div className="text-zinc-500 uppercase tracking-wider">You apply</div>
          <div className="text-zinc-100 font-mono text-base mt-0.5">{coverage.avg_session_inches}"</div>
          <div className="text-zinc-600 text-[10px]">per session per zone</div>
        </div>
        <div className="p-2 bg-zinc-900/60 rounded">
          <div className="text-zinc-500 uppercase tracking-wider">TurfPulse says</div>
          <div className="text-zinc-100 font-mono text-base mt-0.5">{coverage.recommended_session_inches}"</div>
          <div className="text-zinc-600 text-[10px]">per session per zone</div>
        </div>
        {coverage.gap_in > 0 && (
          <div className="p-2 bg-zinc-900/60 rounded">
            <div className="text-zinc-500 uppercase tracking-wider">Gap</div>
            <div className={`${palette.accent} font-mono text-base mt-0.5`}>+{coverage.extra_minutes_per_zone} min</div>
            <div className="text-zinc-600 text-[10px]">/ zone / session</div>
          </div>
        )}
      </div>

      <p className="text-zinc-300 text-sm leading-relaxed">{coverage.message}</p>

      {coverage.verdict === 'under' && (
        <div className="text-zinc-500 text-xs italic leading-relaxed">
          Don't sweat it — your turf is probably fine right now thanks to recent rain. This number tells you what you'd need if it stopped raining. Soil moisture sensors (when installed) will replace this guess with measured ground truth.
        </div>
      )}
    </div>
  );
};

/**
 * ApplyToRachioPanel — pushes computed Water Budget runtimes to Rachio in
 * one click via the public start_multiple endpoint. Self-hides when the
 * user hasn't connected a Rachio account yet.
 *
 * Maps Water Budget zones (user-named) → Rachio zones (controller-named)
 * by case-insensitive substring match, then lets the user override per-row.
 */
const ApplyToRachioPanel = ({ wbZones, grassType, soilType }) => {
  const [rachioZones, setRachioZones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [persisted, setPersisted] = useState(false);
  const [mapping, setMapping] = useState({}); // wbIdx -> rachioId
  const [applying, setApplying] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedName, setSchedName] = useState('My TurfPulse Schedule');
  const [schedDays, setSchedDays] = useState([1, 3]); // Tue, Thu by default
  const [schedTime, setSchedTime] = useState('06:00');
  const [skipRain, setSkipRain] = useState(0.25);
  const [skipBlackout, setSkipBlackout] = useState(true);
  const [splitStrategy, setSplitStrategy] = useState('smart_gap'); // default to smart split — pro-grade
  const [splitRecommendation, setSplitRecommendation] = useState(null);
  const [splitTouchedByUser, setSplitTouchedByUser] = useState(false);

  // Auto-suggest the right split based on grass + soil. We pre-select the
  // recommended strategy once on mount; if the user manually changes it later
  // we stop overriding their choice (splitTouchedByUser flag).
  useEffect(() => {
    if (!grassType && !soilType) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/irrigation/schedules/recommend-strategy`,
          { params: { grass_type: grassType, soil_type: soilType } },
        );
        if (!alive) return;
        setSplitRecommendation(r.data);
        if (!splitTouchedByUser && r.data?.strategy) {
          setSplitStrategy(r.data.strategy);
        }
      } catch {
        /* silent — fall back to default */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grassType, soilType]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await axios.get(`${API_URL}/api/rachio/persisted`, { withCredentials: true });
        if (!p.data?.has_token) { if (alive) { setPersisted(false); setLoading(false); } return; }
        if (alive) setPersisted(true);
        const z = await axios.get(`${API_URL}/api/rachio/zones`, { withCredentials: true });
        if (alive) setRachioZones(z.data?.device?.zones || []);
      } catch {
        if (alive) setPersisted(false);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-map when zones load (case-insensitive substring match in either direction)
  useEffect(() => {
    if (!rachioZones || !wbZones) return;
    const next = {};
    wbZones.forEach((wbz, idx) => {
      const w = (wbz.name || '').toLowerCase().trim();
      if (!w) return;
      const m = rachioZones.find((rz) => {
        const r = (rz.name || '').toLowerCase().trim();
        return r === w || r.includes(w) || w.includes(r);
      });
      if (m) next[idx] = m.id;
    });
    setMapping(next);
  }, [rachioZones, wbZones]);

  if (loading) return null;

  // Empty-state CTA when user is logged out — turns Apply panel into a
  // conversion magnet instead of silently disappearing.
  if (!persisted) {
    return (
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-xl p-5 space-y-3" data-testid="wb-rachio-cta-locked">
        <div className="flex items-start gap-2">
          <Plugs size={18} weight="fill" className="text-amber-400 mt-0.5" />
          <div>
            <div className="text-amber-400 text-xs uppercase tracking-[0.2em] font-semibold">Push to your controller</div>
            <div className="text-zinc-200 font-semibold mt-0.5">Skip the manual entry — let TurfPulse run this plan for you.</div>
            <p className="text-zinc-400 text-xs mt-2 leading-relaxed">
              Connect your <span className="text-emerald-400 font-medium">Rachio</span> once and TurfPulse will push this plan in one click —
              or set it to recur and we'll auto-skip on rain forecast, city blackouts, and (soon)
              soil moisture readings. No more weekly transcription.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Link
            to="/app"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg flex items-center gap-1.5"
            data-testid="wb-rachio-cta-signin"
          >
            Sign in →
          </Link>
          <Link
            to="/app"
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-medium rounded-lg flex items-center gap-1.5"
            data-testid="wb-rachio-cta-signup"
          >
            Create free account
          </Link>
          <Link
            to="/kit"
            className="px-4 py-2 text-zinc-400 hover:text-zinc-200 text-sm rounded-lg flex items-center gap-1.5"
            data-testid="wb-rachio-cta-buy"
          >
            Don't have a Rachio? See gear →
          </Link>
        </div>
      </div>
    );
  }
  if (!rachioZones || rachioZones.length === 0) return null;

  const runnable = wbZones
    .map((wbz, idx) => ({ idx, wbz, rachioId: mapping[idx] }))
    .filter((x) => x.rachioId && x.wbz.runtime_minutes > 0);
  const totalMinutes = runnable.reduce((sum, x) => sum + x.wbz.runtime_minutes, 0);

  const apply = async () => {
    if (runnable.length === 0) {
      toast.error('Map at least one zone first.');
      return;
    }
    if (!window.confirm(
      `Run ${runnable.length} zone(s) in sequence on your Rachio NOW?\n\n` +
      `This single session: ~${totalMinutes} minutes of watering.`
    )) return;
    setApplying(true);
    try {
      const payload = {
        zones: runnable.map((x, i) => ({
          id: x.rachioId,
          duration: Math.round(x.wbz.runtime_minutes * 60),
          sortOrder: i + 1,
        })),
      };
      await axios.post(`${API_URL}/api/rachio/zone/start_multiple`, payload, { withCredentials: true });
      toast.success(`${runnable.length} zones queued — Rachio is starting now.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const saveAsSchedule = async () => {
    if (runnable.length === 0) {
      toast.error('Map at least one zone first.');
      return;
    }
    if (schedDays.length === 0) {
      toast.error('Pick at least one day of the week.');
      return;
    }
    setSavingSchedule(true);
    try {
      // Look up zone names for display
      const zoneById = {};
      rachioZones.forEach(z => { zoneById[z.id] = z.name; });
      const payload = {
        name: schedName,
        zones: runnable.map((x, i) => ({
          rachio_zone_id: x.rachioId,
          duration_minutes: x.wbz.runtime_minutes,
          sort_order: i + 1,
          name: zoneById[x.rachioId] || x.wbz.name,
        })),
        days_of_week: [...schedDays].sort((a, b) => a - b),
        start_time_local: schedTime,
        timezone: 'America/Chicago',
        smart_skip: {
          skip_if_rain_forecast_in: skipRain,
          skip_if_in_blackout: skipBlackout,
        },
        split_strategy: splitStrategy,
      };
      await axios.post(`${API_URL}/api/irrigation/schedules`, payload, { withCredentials: true });
      toast.success(`Schedule "${schedName}" saved — TurfPulse will run it automatically.`);
      setShowSchedule(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSavingSchedule(false);
    }
  };

  return (
    <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-5 space-y-4" data-testid="wb-apply-rachio-panel">
      <div className="flex items-start gap-2">
        <Plugs size={18} weight="fill" className="text-emerald-400 mt-0.5" />
        <div>
          <div className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold">Apply directly to Rachio</div>
          <div className="text-zinc-200 font-semibold mt-0.5">Push these runtimes to your controller in one click.</div>
          <div className="text-zinc-500 text-xs mt-1">
            We'll match each Water Budget zone to a Rachio zone. Override below if a row is wrong.
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {wbZones.map((wbz, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2 rounded border border-zinc-800 bg-zinc-900/40 text-sm" data-testid={`wb-rachio-map-${idx}`}>
            <div className="flex-1 min-w-0">
              <div className="text-zinc-200 font-medium truncate">{wbz.name}</div>
              <div className="text-zinc-500 text-xs">{wbz.runtime_minutes} min · {wbz.precipitation_rate_in_hr} in/hr</div>
            </div>
            <ArrowRight size={14} className="text-zinc-600 flex-shrink-0" />
            <select
              value={mapping[idx] || ''}
              onChange={(e) => setMapping((m) => ({ ...m, [idx]: e.target.value }))}
              className="flex-1 min-w-0 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-xs"
              data-testid={`wb-rachio-select-${idx}`}
            >
              <option value="">— skip this zone —</option>
              {rachioZones.map((rz) => (
                <option key={rz.id} value={rz.id}>
                  Z{rz.zone_number} · {rz.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-emerald-500/20">
        <div className="text-xs text-zinc-400">
          {runnable.length} of {wbZones.length} mapped · ~{totalMinutes} min per session
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSchedule((s) => !s)}
            disabled={runnable.length === 0}
            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-100 text-xs font-medium rounded-lg flex items-center gap-1.5"
            data-testid="wb-save-schedule-toggle"
          >
            <Timer size={14} />
            {showSchedule ? 'Cancel' : 'Save as recurring'}
          </button>
          <button
            onClick={apply}
            disabled={applying || runnable.length === 0}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg flex items-center gap-2"
            data-testid="wb-apply-rachio-btn"
          >
            {applying ? <ArrowClockwise size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
            {applying ? 'Pushing to Rachio…' : 'Run plan on Rachio now'}
          </button>
        </div>
      </div>

      {showSchedule && (
        <div className="border-t border-emerald-500/20 pt-4 space-y-3" data-testid="wb-schedule-form">
          <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Recurring schedule</div>

          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Name</label>
            <input
              type="text"
              value={schedName}
              onChange={(e) => setSchedName(e.target.value)}
              maxLength={80}
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-sm"
              data-testid="wb-sched-name"
            />
          </div>

          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Days of week</label>
            <div className="flex gap-1 flex-wrap">
              {[
                { i: 0, label: 'M' }, { i: 1, label: 'T' }, { i: 2, label: 'W' },
                { i: 3, label: 'Th' }, { i: 4, label: 'F' }, { i: 5, label: 'Sa' }, { i: 6, label: 'Su' },
              ].map((d) => {
                const active = schedDays.includes(d.i);
                return (
                  <button
                    key={d.i}
                    type="button"
                    onClick={() =>
                      setSchedDays((days) =>
                        active ? days.filter((x) => x !== d.i) : [...days, d.i],
                      )
                    }
                    className={
                      'w-9 h-9 rounded-md text-xs font-semibold border ' +
                      (active
                        ? 'bg-emerald-600 border-emerald-500 text-white'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500')
                    }
                    data-testid={`wb-sched-day-${d.i}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Start time (local)</label>
            <input
              type="time"
              value={schedTime}
              onChange={(e) => setSchedTime(e.target.value)}
              className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-sm"
              data-testid="wb-sched-time"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-zinc-400">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipBlackout}
                onChange={(e) => setSkipBlackout(e.target.checked)}
                data-testid="wb-sched-skip-blackout"
              />
              Skip during city watering blackout
            </label>
            <label className="flex items-center gap-2">
              <input
                type="number"
                step="0.05"
                min="0"
                max="2"
                value={skipRain}
                onChange={(e) => setSkipRain(parseFloat(e.target.value) || 0)}
                className="w-20 bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-zinc-100"
                data-testid="wb-sched-skip-rain"
              />
              <span>" rain forecast → skip</span>
            </label>
          </div>

          {/* Split strategy — Smart gap (deeper soak before longer dry gap)
              vs Even (every session same). Smart is the pro-grade default. */}
          <div className="border-t border-emerald-500/20 pt-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Session split strategy</div>
              {splitRecommendation && (
                <div className="text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5" data-testid="wb-split-recommendation-badge">
                  ✨ TurfPulse picks: {splitRecommendation.strategy === 'smart_gap' ? 'Smart Split' : 'Even Split'}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setSplitStrategy('smart_gap'); setSplitTouchedByUser(true); }}
                className={
                  'p-3 rounded-lg border text-left text-xs transition relative ' +
                  (splitStrategy === 'smart_gap'
                    ? 'bg-emerald-600/20 border-emerald-500 text-emerald-100'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500')
                }
                data-testid="wb-split-smart"
              >
                <div className="font-semibold flex items-center gap-1.5">
                  Smart gap-weighted ✨
                  {splitRecommendation?.strategy === 'smart_gap' && (
                    <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 bg-emerald-500/30 text-emerald-200 rounded">Pick</span>
                  )}
                </div>
                <div className="text-[10px] mt-0.5 opacity-80 leading-relaxed">
                  Deeper soak before longer dry stretch. Encourages deeper roots — pro turf approach.
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setSplitStrategy('even'); setSplitTouchedByUser(true); }}
                className={
                  'p-3 rounded-lg border text-left text-xs transition relative ' +
                  (splitStrategy === 'even'
                    ? 'bg-emerald-600/20 border-emerald-500 text-emerald-100'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500')
                }
                data-testid="wb-split-even"
              >
                <div className="font-semibold flex items-center gap-1.5">
                  Even split
                  {splitRecommendation?.strategy === 'even' && (
                    <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 bg-emerald-500/30 text-emerald-200 rounded">Pick</span>
                  )}
                </div>
                <div className="text-[10px] mt-0.5 opacity-80 leading-relaxed">
                  Every allowed day uses the same runtime. Simple and predictable.
                </div>
              </button>
            </div>

            {splitRecommendation?.reason && (
              <p className="text-[10px] text-zinc-500 italic leading-relaxed pt-1" data-testid="wb-split-reason">
                {splitRecommendation.reason}
              </p>
            )}

            {/* Live preview of per-day actual runtimes */}
            <SplitPreview
              schedDays={schedDays}
              schedTime={schedTime}
              splitStrategy={splitStrategy}
              schedName={schedName}
              runnable={runnable}
              zoneById={Object.fromEntries(rachioZones.map((z) => [z.id, z.name]))}
            />
          </div>

          <button
            onClick={saveAsSchedule}
            disabled={savingSchedule}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg flex items-center gap-2"
            data-testid="wb-sched-save-btn"
          >
            {savingSchedule ? <ArrowClockwise size={14} className="animate-spin" /> : <CheckCircle size={14} weight="fill" />}
            {savingSchedule ? 'Saving…' : 'Save schedule'}
          </button>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            TurfPulse will run this plan on the days &amp; time you picked. Smart-skip checks rain
            forecast and city blackouts so you never water when you shouldn't. Manage or pause it
            anytime from the Schedules tab.
          </p>
        </div>
      )}
    </div>
  );
};

export default WaterBudgetPage;

/**
 * SavedSchedulesPanel — lists user's recurring irrigation schedules.
 * Pause / resume / delete inline. Self-hides when user has none.
 */
const SavedSchedulesPanel = () => {
  const [schedules, setSchedules] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/irrigation/schedules`, { withCredentials: true });
      setSchedules(r.data?.schedules || []);
    } catch {
      setSchedules([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    setBusy((b) => ({ ...b, [s.id]: true }));
    try {
      await axios.put(
        `${API_URL}/api/irrigation/schedules/${s.id}`,
        { enabled: !s.enabled },
        { withCredentials: true },
      );
      toast.success(`${s.name} ${s.enabled ? 'paused' : 'resumed'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy((b) => ({ ...b, [s.id]: false }));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"? This cannot be undone.`)) return;
    setBusy((b) => ({ ...b, [s.id]: true }));
    try {
      await axios.delete(`${API_URL}/api/irrigation/schedules/${s.id}`, { withCredentials: true });
      toast.success(`Deleted "${s.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy((b) => ({ ...b, [s.id]: false }));
    }
  };

  if (schedules === null || schedules.length === 0) return null;

  const dayLabel = (d) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d] || '?';

  return (
    <section className="max-w-5xl mx-auto px-5 md:px-8 py-6" data-testid="wb-saved-schedules">
      <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Timer size={16} className="text-emerald-400" />
          <div className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold">
            Your recurring irrigation schedules
          </div>
        </div>
        {schedules.map((s) => {
          const totalMin = (s.zones || []).reduce((acc, z) => acc + (z.duration_minutes || 0), 0);
          const next = s.next_fire_at ? new Date(s.next_fire_at).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          }) : '—';
          return (
            <div key={s.id} className="border border-zinc-800 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`wb-sched-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-100 font-semibold truncate">{s.name}</span>
                  {!s.enabled && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded">PAUSED</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {(s.days_of_week || []).map(dayLabel).join(' · ')} at {s.start_time_local} · {s.zones?.length || 0} zones · ~{totalMin} min
                </div>
                <div className="text-[10px] text-zinc-600 mt-0.5">
                  Next: {next}
                  {s.last_skip_reason && <span className="text-amber-500"> · last skip: {s.last_skip_reason}</span>}
                  {s.fire_count > 0 && <span> · {s.fire_count} runs</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggle(s)}
                  disabled={busy[s.id]}
                  className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded disabled:opacity-50"
                  data-testid={`wb-sched-toggle-${s.id}`}
                >
                  {s.enabled ? 'Pause' : 'Resume'}
                </button>
                <button
                  onClick={() => remove(s)}
                  disabled={busy[s.id]}
                  className="px-3 py-1.5 text-xs bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded disabled:opacity-50"
                  data-testid={`wb-sched-delete-${s.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
